import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { Save, ArrowLeft } from 'lucide-react';
import type { Role, RoleCreate } from '@kss/api';
import { roleService } from '@kss/api';

const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls = 'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function NewRole() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const isEdit = !!id && id !== 'new';
  const [form, setForm] = useState({ name: '', code: '', description: '', isActive: true });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let c = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setErr(null);
        try { const r = await roleService.get(id); if (!c) setForm({ name: r.name ?? '', code: r.code ?? '', description: r.description ?? '', isActive: !!r.isActive }); }
        catch (e) { const msg = e instanceof Error ? e.message : 'Not found'; setErr(msg); toastRef.current.error('Load failed', msg); }
        finally { if (!c) setLoading(false); }
      }
    }
    run(); return () => { c = true; };
  }, [id, isEdit]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Fix errors', 'Name is required'); return; }
    setSaving(true);
    try {
      const base = { name: form.name, code: form.code || undefined, description: form.description || undefined, isActive: form.isActive };
      if (isEdit && id) { await roleService.update([{ roleId: id, ...base } as Role]); toast.success('Saved', `${form.name} updated`); }
      else { await roleService.create([base as RoleCreate]); toast.success('Saved', `${form.name} added`); }
      navigate('/panel/roles');
    } catch (e) { toast.error('Save failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/roles')}><ArrowLeft className="h-4 w-4" /></Button>
          <h2 className="text-base font-semibold text-content-primary">{isEdit ? 'Edit role' : 'New role'}</h2>
        </div>
        {loading ? <div className="text-sm text-content-muted">Loading…</div> : (
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1"><span className="text-xs font-medium text-content-secondary">Name <span className="text-danger">*</span></span>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Manager" className={inputCls} /></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-medium text-content-secondary">Code</span>
              <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="manager" className={inputCls} /></label>
            <label className="flex flex-col gap-1 md:col-span-2"><span className="text-xs font-medium text-content-secondary">Description</span>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} className={`${inputCls} h-auto py-2`} /></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-medium text-content-secondary">Status</span>
              <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === 'active' }))} className={selectCls}>
                <option value="active">active</option><option value="inactive">inactive</option></select></label>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate('/panel/roles')}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.name.trim()}><Save className="h-4 w-4" />{saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}</Button>
        </div>
      </Card>
    </div>
  );
}
